import { useCallback, useEffect, useState } from "react";
import { customGoal, stage } from "@flights/et-universal";
import { useSelector } from "react-redux";
import { getSearchFetchStatus, getSearchFlexPrice, getSearchResultsFlightDeals } from "store/searchResults/selectors";
import { getFlexibleDateMinPrice } from "store/flexibleDateMinPrice/selectors";
import { flightsSessionStore } from "@flights/web-api-utils-universal";
import useRouteName from "hooks/useRouteName";
import useProfiler from "hooks/useProfiler";
import { StoreState } from "store";

export default function useTrackFlightsFlexiBannerSourceChange() {
  const sessionKey = "newFlexiClickedPriceValue";
  const expTagName = "flights_web_sr_new_flex_price_ml_model";

  const routeName = useRouteName();
  const [isTracked, setIsTracked] = useState(false);
  const [canTrack, setCanTrack] = useState(false);
  const searchResultsFetchStatus = useSelector(getSearchFetchStatus);
  const searchResultsFlightDeals = useSelector(getSearchResultsFlightDeals);
  const searchResultsFlexPrice = useSelector(getSearchFlexPrice);
  const flexibleDateMinPrice = useSelector(getFlexibleDateMinPrice);
  const { isEEA, isDirect, isMobile } = useProfiler();
  const flexPriceWebVariant = useSelector((s: StoreState) => s.searchResults.flexPriceWebVariant);

  const setClickedPrice = useCallback((price: string) => {
    flightsSessionStore.set(sessionKey, price, { ttl: "10m" });
  }, []);

  const getClickedPrice = useCallback(() => {
    return Number(flightsSessionStore.get(sessionKey) || 0);
  }, []);

  const removeClickedPrice = useCallback(() => {
    flightsSessionStore.unset(sessionKey);
  }, []);

  const trackStages = useCallback(() => {
    if (flexPriceWebVariant === 0) return;
    if (searchResultsFlexPrice?.length === 0) return;
    if (isTracked || !canTrack) return;

    stage(expTagName, 1);
    if (flexibleDateMinPrice.length > 0) stage(expTagName, 2);
    stage(expTagName, isEEA() ? 3 : 4);
    stage(expTagName, isDirect() ? 5 : 6);
    stage(expTagName, isMobile() ? 8 : 7);

    const clickedPrice = getClickedPrice();

    const bestOffer = searchResultsFlightDeals?.find(({ key }) => key === "BEST");
    const bestPricePerAdult = bestOffer?.travellerPrices?.find(({ travellerType }) => travellerType === "ADULT")
      ?.travellerPriceBreakdown.totalRounded;

    const cheapestOffer = searchResultsFlightDeals?.find(({ key }) => key === "CHEAPEST");
    const cheapestPricePerAdult = cheapestOffer?.travellerPrices?.find(({ travellerType }) => travellerType === "ADULT")
      ?.travellerPriceBreakdown.totalRounded;

    if (!clickedPrice) return;

    if (searchResultsFetchStatus === "success") customGoal(expTagName, 5);

    if (bestPricePerAdult || cheapestPricePerAdult) {
      const bestPriceNum = bestPricePerAdult ? Number(`${bestPricePerAdult.units}.${bestPricePerAdult.nanos}`) : 0;
      const cheapestPriceNum = cheapestPricePerAdult
        ? Number(`${cheapestPricePerAdult.units}.${cheapestPricePerAdult.nanos}`)
        : 0;

      if (bestPriceNum) {
        if (bestPriceNum > clickedPrice) customGoal(expTagName, 1);
        if (bestPriceNum < clickedPrice) customGoal(expTagName, 2);
      }

      if (cheapestPriceNum) {
        if (cheapestPriceNum > clickedPrice) customGoal(expTagName, 3);
        if (cheapestPriceNum < clickedPrice) customGoal(expTagName, 4);
      }

      removeClickedPrice();
      setIsTracked(true);
    }
  }, [
    flexPriceWebVariant,
    isTracked,
    canTrack,
    searchResultsFlightDeals,
    getClickedPrice,
    removeClickedPrice,
    searchResultsFetchStatus,
    searchResultsFlexPrice,
    flexibleDateMinPrice,
    isEEA,
    isDirect,
    isMobile
  ]);

  useEffect(() => {
    if (routeName.includes("searchresults")) setCanTrack(true);
  }, [routeName, setCanTrack]);

  return {
    setClickedPrice,
    trackStages
  };
}
